import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { getProducts } from "@/lib/api";
import { MaskLine, Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import EditorialMarquee from "@/components/EditorialMarquee";
import Footer from "@/components/Footer";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/cfd61421-6bdb-4208-b2a4-14c93fefc66b/images/0692b144de8122c1a373c650d4eb73c866a35a766722f7b4f94d9d72ff42371c.jpeg";
const WORKSHOP_IMG =
  "https://images.unsplash.com/photo-1614494731690-53925976ea29?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const CHAPTERS = [
  { n: "01", t: "Choisir", d: "Sélectionnez vos pièces dans le catalogue et définissez les quantités. Le prix unitaire baisse automatiquement au volume." },
  { n: "02", t: "Envoyer le logo", d: "Déposez votre logo ou votre visuel. Vecteur, PNG haute résolution ou PDF — on gère tous les formats." },
  { n: "03", t: "Recevoir le devis", d: "On revient vers vous sous 24h avec un devis clair, un rendu et les délais de production." },
  { n: "04", t: "Production", d: "Après validation, on lance la personnalisation. Sérigraphie, broderie ou flocage selon le support." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 140]);
  const heroScale = useTransform(scrollY, [0, 600], [1, 1.12]);

  useEffect(() => {
    getProducts().then(setProducts).catch(() => {});
  }, []);

  return (
    <div className="grain">
      {/* HERO */}
      <section className="relative min-h-screen bg-[#0a0a0a] text-white overflow-hidden flex flex-col justify-end">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Vêtements personnalisés" className="w-full h-full object-cover opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-[#0a0a0a]/40" />
        </motion.div>

        <div className="relative max-w-[1600px] mx-auto w-full px-6 md:px-12 pb-16 md:pb-24 pt-40">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-white/60 mb-6"
          >
            Personnalisation de vêtements · B2B
          </motion.p>
          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.82] text-6xl sm:text-7xl md:text-8xl lg:text-[9rem]">
            <MaskLine delay={0.1}>Créez</MaskLine>
            <MaskLine delay={0.24}>votre</MaskLine>
            <MaskLine delay={0.38} className="text-[#0044ff]">marque.</MaskLine>
          </h1>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-8 mt-10">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9 }}
              className="max-w-md text-white/70 text-base md:text-lg leading-relaxed"
            >
              T-shirts, sweats, doudounes, casquettes, mugs. Choisissez, envoyez votre logo,
              recevez un devis dégressif. Simple, rapide, professionnel.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.9 }}
              className="flex items-center gap-4"
            >
              <Link
                to="/#catalogue"
                data-testid="hero-cta"
                className="bg-white text-black px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#0044ff] hover:text-white transition-colors"
              >
                Voir le catalogue
              </Link>
              <ArrowDown className="w-6 h-6 animate-bounce text-white/50" />
            </motion.div>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* CATALOGUE */}
      <section id="catalogue" className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-40">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#0044ff] mb-4">Le catalogue</p>
              <h2 className="font-display font-black uppercase tracking-tighter leading-[0.85] text-5xl md:text-7xl">
                Support<br />vierge, <span className="text-[#0044ff]">signature</span><br />unique.
              </h2>
            </div>
            <p className="max-w-sm text-[#555]">
              Des pièces sélectionnées pour la personnalisation. Chaque produit affiche un tarif dégressif :
              plus vous commandez, moins c'est cher.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
          {products.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* MANIFESTO / PROCESS */}
      <section id="process" className="bg-[#0a0a0a] text-white">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-40">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-4">La méthode</p>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.85] text-5xl md:text-7xl mb-20">
              Quatre étapes,<br />zéro friction.
            </h2>
          </Reveal>
          <div className="space-y-2">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.05}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-10 border-t border-white/10 items-start group">
                  <span className="md:col-span-3 font-display font-black text-7xl md:text-8xl text-white/10 group-hover:text-[#0044ff] transition-colors duration-500">
                    {c.n}
                  </span>
                  <h3 className="md:col-span-4 font-display font-bold text-3xl md:text-4xl tracking-tight">{c.t}</h3>
                  <p className="md:col-span-5 text-white/60 text-lg leading-relaxed md:pt-2">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="relative h-[50vh] overflow-hidden">
          <img src={WORKSHOP_IMG} alt="Atelier de sérigraphie" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl text-center px-6">
              Fabriqué avec précision.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
